import { getMobileWidgetDefinition } from '../../analyticsWidgets';
import { useDashboardFilters } from '../../utils/user/dashboardTimeRange';
import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';
import { useDispatch, useSelector } from 'react-redux';
import API from '../../services/api';
import HeaderPDF from '../../components/header/HeaderPDF';
import Loading from '../../components/loading';
import DateTimeFilterBar from '../../components/dateTimeFilterBar';
import { getUser } from '../../redux/user/actions';
import SidePanel from '../../components/sidepanel';
import AnalyticsSelector from '../../components/analyticsSelector/brandAnalyticsSelector';
import WidgetGrid from '../analytics/widgetGrid';
import { getFilteredWidgets } from '../../utils/widgets';
import utils from '../../utils';
import { setAlert } from '../../redux/dashboard/actions';
import calendar from '../../utils/calendar';
import Timezone from '../../components/timezone';
import CustomDropdown from '../../components/customDropdown';
import GenerateReportButton from './generateReportButton';
import reportUtils from '../../utils/report';

const BrandChartsContainer = ({ lastUpdated, selectedDashboard, portal }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteIds, setSiteIds] = useState([]);
  const [devices, setDevices] = useState([]);
  const [appliedFilters, setAppliedFilters] = useDashboardFilters({});
  const [collapseSidebar, setCollapseSidebar] = useState(true);
  const [currentWidgets, setCurrentWidgets] = useState({});
  const [filteredWidgets, setFilteredWidgets] = useState([]);
  const [heatmapDevices] = useState([]);
  const [wifiDevices] = useState([]);
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [speedUnit, setSpeedUnit] = useState('KM_PER_HOUR');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [hasTable, setHasTable] = useState(true);

  const eventHandlers = useRef([]);
  const oldPortalRef = useRef();
  const oldDashboardRef = useRef();

  const user = useSelector(getUser);
  const dispatch = useDispatch();

  const defaultPreferences = {
    maxSessionTime: 120,
    maxDwellTime: 60,
    viewThreshold: 0.5,
    viewMetric: 'ATTENTION',
    minConfidenceGender: 0.1,
    maxConfidenceGender: 1,
    minConfidenceAge: 0.1,
    maxConfidenceAge: 1,
    minConfidenceEmotion: 0.1,
    maxConfidenceEmotion: 1,
    enableAnomalyDetection: true,
    vehicleMultiplier: {
      car: 2,
      motorcycle: 1,
      bus: 35,
      truck: 2
    }
  };

  useEffect(() => {
    return () => {
      eventHandlers.current.forEach(handler => {
        window.removeEventListener('requestResult', handler);
        window.removeEventListener('requestError', handler);
      });
    };
  }, []);

  useEffect(() => {
    setAppliedFilters(prevState => ({ ...prevState, timezone }));
  }, [timezone]);

  useEffect(() => {
    const newConfig = { ...defaultPreferences, speedUnit: speedUnit };

    setAppliedFilters(prevState => {
      const prevBrand = prevState?.brandCompany || {};
      const newBrandCompany = {
        ...prevBrand,
        isBrandPortal: true,
        preferences: newConfig
      };
      return {
        ...prevState,
        bpid: newBrandCompany.id,
        brandCompany: newBrandCompany
      };
    });
  }, [speedUnit]);

  useEffect(() => {
    if (portal && user && selectedDashboard) {
      setLoadingDashboard(true);
      const startDate = !oldPortalRef.current ? calendar.getDefaultDate('start') : appliedFilters.startDate;
      const endDate = !oldPortalRef.current ? calendar.getDefaultDate('end') : appliedFilters.endDate;
      const interval = !oldPortalRef.current ? calendar.getIntervalById('day') : appliedFilters.interval;

      // Load Filters
      setAppliedFilters({
        brandCompany: {
          id: portal.id,
          companyId: portal.id,
          preferences: { ...defaultPreferences, speedUnit: speedUnit },
          isBrandPortal: true
        },
        bpid: portal.id,
        startDate,
        endDate,
        timezone: timezone,
        interval,
        filterByTime: {},
        filterByWeekday: [],
        selectedDevices: devices,
        wifiDevices,
        heatmapDevices,
        selectedSites: sites,
        selectedMedias: [],
        selectedAdvertisers: [],
        selectedSolution,
        selectedCampaigns: campaigns,
        // mobile data extra params
        sites: siteIds,
        startTime: startDate, // Naming convention is different on bigQuery
        endTime: endDate // Naming convention is different on bigQuery
      });

      if (portal.id !== oldPortalRef.current) {
        loadSolution();
      }

      oldPortalRef.current = portal.id;
    } else {
      setLoadingDashboard(false);
    }
  }, [portal, user]);

  // Defensive: if brandCompany is lost during flow restore it from `portal` when available
  useEffect(() => {
    if (!portal) return;
    setAppliedFilters(prev => {
      const bc = prev?.brandCompany;
      const hasBrand = bc && bc.id;
      const preferences = (bc && bc.preferences) || { ...defaultPreferences, speedUnit };

      const defaultStart = calendar.getDefaultDate('start');
      const defaultEnd = calendar.getDefaultDate('end');
      const defaultInterval = calendar.getIntervalById('day');

      // If brandCompany, dates or interval are already present, keep them.
      const next = {
        ...prev,
        brandCompany: hasBrand
          ? bc
          : {
              id: portal.id,
              companyId: portal.id,
              isBrandPortal: true,
              preferences
            },
        timezone: prev?.timezone || timezone,
        bpid: portal.id
      };

      if (!prev?.interval) next.interval = defaultInterval;
      if (!prev?.startDate) next.startDate = defaultStart;
      if (!prev?.endDate) next.endDate = defaultEnd;
      if (!prev?.filterByTime) next.filterByTime = {};
      if (!prev?.filterByWeekday) next.filterByWeekday = [];

      return next;
    });
  }, [portal]);

  useEffect(() => {
    const fetchWidgets = async () => {
      setCollapseSidebar(true);
      if (selectedDashboard && selectedDashboard.id !== oldDashboardRef.current) {
        setHasTable(true);
        setLoadingDashboard(true);
        // Load Widgets
        let widgets = {};

        const widgetsToFetch = [];
        selectedDashboard.rows.forEach(dashboardRow => {
          dashboardRow.widgets.forEach(widgetData => {
            widgetsToFetch.push(widgetData);
          });
        });

        const fetchedWidgets = await Promise.all(
          widgetsToFetch.map(async widgetData => {
            const widget = getMobileWidgetDefinition(widgetData, selectedDashboard) || await API.getWidget(widgetData.url);
            return { ...widget, widgetUrl: widgetData.url };
          })
        );

        fetchedWidgets.forEach(fetchedWidget => {
          widgets = { ...widgets, [fetchedWidget.id]: fetchedWidget };
        });

        setCurrentWidgets(widgets);

        // Immediately compute filtered widgets from the freshly fetched widgets
        try {
          const widgetsInPlace = selectedDashboard.rows.map(dashboardRow =>
            dashboardRow.widgets.map(widgetData => widgets[widgetData.id] || { id: widgetData.id })
          );
          const customWidgets =
            user?.preferences?.customWidgets && selectedDashboard
              ? user?.preferences?.customWidgets[selectedDashboard.id]
              : null;
          const newFilteredWidgets = getFilteredWidgets(widgetsInPlace, customWidgets);
          setFilteredWidgets(newFilteredWidgets);
        } catch (e) {
          // fallback: do nothing if something unexpected happens
        }

        setLoadingDashboard(false);

        oldDashboardRef.current = selectedDashboard.id;
      }
    };

    fetchWidgets();
    loadSolution();

  }, [selectedDashboard]);

  useEffect(() => {
    // Only load widgets when appliedFilters is populated and interval is available.
    // This prevents early calls where interval is undefined and causes empty results.
    if (appliedFilters && appliedFilters.interval) {
      loadWidgets();
    }
  }, [appliedFilters]);

  useEffect(() => {
    loadSites();
    setAppliedFilters(prevState => {
      return {
        ...prevState,
        selectedCampaigns: campaigns
      };
    });
  }, [campaigns]);

  useEffect(() => {
    if (appliedFilters) {
      setAppliedFilters(prevState => {
        return { ...prevState, wifiDevices, heatmapDevices };
      });
    }
  }, [heatmapDevices, wifiDevices]);

  useEffect(() => {
    let cancelled = false;
    setCampaigns([]);
    if (portal?.id) {
      const filter = encodeURI(`brandPortalId = "${portal.id}"`);
      API.getCampaigns(portal.companyId, '', filter)
        .then(res => { if (!cancelled) setCampaigns(res.campaigns || []); })
        .catch(() => {
          if (!cancelled) dispatch(setAlert(utils.generateAlert('Unable to load campaigns. Please try again.', 'error')));
        });
    }
    return () => { cancelled = true; };
  }, [portal?.id, portal?.companyId, lastUpdated]);

  useEffect(() => {
    let cancelled = false;
    setDevices([]);
    if (portal?.id) {
      API.getDevicesByBrandPortal(portal.id)
        .then(res => { if (!cancelled) setDevices(res); })
        .catch(() => {
          if (!cancelled) dispatch(setAlert(utils.generateAlert('Unable to load campaign devices. Please try again.', 'error')));
        });
    }
    return () => { cancelled = true; };
  }, [portal?.id, lastUpdated]);

  useEffect(() => {
    setAppliedFilters(prevState => {
      const newState = {
        ...prevState,
        selectedSolution
      };
      return newState;
    });
  }, [selectedSolution]);

  useEffect(() => {
    campaigns.forEach(campaign => {
      campaign.devices = devices.filter(device =>
        (campaign.sites || []).some(site => device.attachedSites?.includes(site.id))
      );
    });
    setAppliedFilters(prev => ({
      ...prev,
      selectedDevices: devices.filter(device =>
        (prev.sites || []).some(id => device.attachedSites?.includes(id))
      )
    }));
  }, [devices, campaigns]);

  useEffect(() => {
    const widgetCount = getWidgetCount();
    if (widgetCount > 0 && Object.keys(currentWidgets).length === widgetCount) {
      loadWidgets();
    }
  }, [currentWidgets]);

  const loadWidgets = () => {
    // Load Widgets

    const customWidgets =
      user?.preferences?.customWidgets && selectedDashboard
        ? user?.preferences?.customWidgets[selectedDashboard.id]
        : null;
    const widgetsInPlace = getWidgetsInPlace();

    const newFilteredWidgets = getFilteredWidgets(widgetsInPlace, customWidgets);

    setFilteredWidgets(newFilteredWidgets);
  };

  const loadSites = () => {
    if (!campaigns || campaigns.length === 0) {
      setSiteIds([]);
      setSites([]);
      setAppliedFilters(prevState => {
        return { ...prevState, sites: [], selectedSites: [] };
      });
      return;
    }

    const localSites = [];
    campaigns.forEach(campaign => {
      if (Array.isArray(campaign.sites)) {
        localSites.push(...campaign.sites);
      }
    });

    const uniqueSitesMap = new Map();
    localSites.forEach(site => {
      if (site && site.id && !uniqueSitesMap.has(site.id)) {
        uniqueSitesMap.set(site.id, site);
      }
    });

    const sitesArray = Array.from(uniqueSitesMap.values());
    const sitesIdsArray = sitesArray.map(site => site.id);

    setSiteIds(sitesIdsArray);
    setSites(sitesArray);
    setAppliedFilters(prevState => {
      return { ...prevState, sites: sitesIdsArray, selectedSites: sitesArray };
    });
  };

  const loadSolution = async () => {
    if (portal && selectedDashboard) {
      await API.getTableSchema(portal.id, selectedDashboard.table)
        .then(res => {
          setSelectedSolution({
            id: selectedDashboard.headers,
            schema: res.schema,
            isCms: selectedDashboard.isCms,
            table: selectedDashboard.table
          });
        })
        .catch(error => {
          console.error(error);
          return [];
        })
        .finally(() => {
          setLoadingDashboard(false);
        });
    }
    return [];
  };

  const onFilterSelect = payload => {
    setAppliedFilters(prev => {
      const next = { ...prev, ...payload };
      if (payload.selectedSites) {
        next.sites = payload.selectedSites.map(site => site.id);
        next.selectedDevices = devices.filter(device =>
          next.sites.some(id => device.attachedSites?.includes(id))
        );
      }
      // Normalize interval: store as id string
      if (Object.prototype.hasOwnProperty.call(payload, 'interval')) {
        // payload.interval may be an id string (from IntervalSelector) or an interval object
        next.interval =
          typeof payload.interval === 'string' ? calendar.getIntervalById(payload.interval) : payload.interval;
      }
      // Ensure mobile param names are synced
      if (payload.startDate) next.startTime = payload.startDate;
      if (payload.endDate) next.endTime = payload.endDate;
      // Preserve existing filterByTime/filterByWeekday if payload doesn't include them
      if (Object.prototype.hasOwnProperty.call(payload, 'filterByTime')) {
        next.filterByTime = payload.filterByTime || {};
      } else {
        next.filterByTime = prev?.filterByTime || {};
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'filterByWeekday')) {
        next.filterByWeekday = payload.filterByWeekday || [];
      } else {
        next.filterByWeekday = prev?.filterByWeekday || [];
      }
      // Preserve brandCompany if not provided
      if (!Object.prototype.hasOwnProperty.call(payload, 'brandCompany') && prev?.brandCompany) {
        next.brandCompany = prev.brandCompany;
      }

      return next;
    });
  };

  const getWidgetCount = () => {
    if (!selectedDashboard || !selectedDashboard.rows) return 0;

    return selectedDashboard.rows.map(row => row.widgets.length).reduce((sum, prev) => sum + prev);
  };

  const getWidgetsInPlace = () => {
    if (!selectedDashboard || !selectedDashboard.rows || Object.keys(currentWidgets).length === 0) {
      return [];
    }

    return selectedDashboard.rows.map(widgetRow => {
      return widgetRow.widgets.map(widget => currentWidgets[widget.id]);
    });
  };

  const onError = e => {
    const { code, message } = e.detail;
    let errorMessage = message ? message : 'Something went wrong. Please try again later.';

    if (code === 5) {
      errorMessage = 'Integration is not active for your Campaign. Activate it on Integration Settings';
      setHasTable(false);
    }

    dispatch(setAlert(utils.generateAlert(errorMessage ? errorMessage : 'Something went wrong.', 'error')));
  };

  const renderTopFilterBar = () => {
    if (!appliedFilters) {
      return null;
    }

    return (
      <div className="filter-bar">
        <div className="filters">
          <DateTimeFilterBar
            appliedFilters={{
              startDate: appliedFilters.startDate,
              endDate: appliedFilters.endDate,
              interval:
                appliedFilters.interval?.id || appliedFilters.interval || calendar.getIntervalById('day')?.id || 'day'
            }}
            onPeriodSelected={onFilterSelect}
          />
          <div className="preference-control">
            <Timezone label="Timezone" leftAlign onSelect={setTimezone}
              selected={{ value: appliedFilters.timezone, label: appliedFilters.timezone }}
              disabled={Boolean(appliedFilters.isTimeRangeLocked && user?.preferences?.timezone)} />
            {selectedDashboard?.headers === 'traffic' && (
              <CustomDropdown
                label="Speed Unit"
                items={[
                  { id: 'KM_PER_HOUR', name: 'Kilometers/Hour' },
                  { id: 'MILES_PER_HOUR', name: 'Miles/Hour' },
                  { id: 'METERS_PER_SECOND', name: 'Meters/Second' }
                ]}
                handleSelection={e => setSpeedUnit(e.target.value)}
                valueField={'id'}
                displayField={'name'}
              />
            )}
          </div>
        </div>
        <div className="d-flex">
          {
            <GenerateReportButton
              project={appliedFilters && appliedFilters.brandCompany ? appliedFilters.brandCompany : null}
              report={reportUtils.getCustomReportTemplate(appliedFilters, user?.email)}
              selectedTemplate={selectedDashboard}
            />
          }
        </div>
      </div>
    );
  };

  const renderEmptyMessage = () => {
    return (
      <div className="no-data-message flex-column">
        <h1>No data found!</h1>
        <p>Nothing to see here. Try choosing another site group or creating a new one.</p>
      </div>
    );
  };

  const renderContent = () => {
    const isMobileTemplate =
      selectedDashboard &&
      selectedDashboard.compatibleDatasources &&
      selectedDashboard?.compatibleDatasources?.includes('MOBILE_M');

    if (!selectedDashboard) {
      return null;
    }

    // If there are no widgets to show or table disabled, show empty message.
    // Allow rendering the grid if `filteredWidgets` already contains rows (some widgets don't need devices).
    const hasFilteredWidgets = Array.isArray(filteredWidgets) && filteredWidgets.length > 0;
    if ((!hasFilteredWidgets && devices.length === 0 && !isMobileTemplate) || !hasTable) {
      return renderEmptyMessage();
    }

    return (
      <div className="d-flex">
        <SidePanel
          keepMounted
          isOpen={false}
          customClasses={'brandPortal'}
          content={
            <AnalyticsSelector
              campaigns={campaigns}
              selectedDashboard={selectedDashboard}
              sites={sites}
              appliedFilters={{
                startDate: appliedFilters.startDate,
                endDate: appliedFilters.endDate,
                selectedSites: appliedFilters.selectedSites,
                selectedDevices: appliedFilters.selectedDevices,
                selectedAdvertisers: appliedFilters.selectedAdvertisers,
                selectedMedias: appliedFilters.selectedMedias,
                selectedCampaigns: appliedFilters.selectedCampaigns
              }}
              onSelection={onFilterSelect}
              cmsIntegration={selectedDashboard.isCms ? selectedDashboard.cmsId : null}
            />
          }
          onToggle={() => setCollapseSidebar(!collapseSidebar)}
        />
        <div className="scrollable-container">
          <div id="analytics-chart-container" className={`print ${!collapseSidebar ? 'expanded-sidebar' : ''}`}>
            <WidgetGrid
              appliedFilters={appliedFilters} //this uses solution
              filteredWidgets={filteredWidgets}
              handleError={onError}
              eventHandlers={eventHandlers.current}
              lastUpdated={lastUpdated}
              customDashboard={selectedDashboard}
              isBrand={true}
              portal={portal}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="parent-container analytics-container">
      {/* We are downloading the image so when the user generates the PDF the image already exists for the browser */}
      <div className="company-logo-img visibility-hidden" />
      <HeaderPDF filters={appliedFilters} devices={devices} />
      <div id="analytics-widget-container" className="d-flex flex-column">
        {renderTopFilterBar()}
        {!loadingDashboard ? (
          renderContent()
        ) : (
          <div className="h-100">
            <Loading size={8} />
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandChartsContainer;
